import { useState, useEffect } from "react";

import { useDispatch } from "react-redux";
import { setLogin, setSubscriptionStatus, setOnboardingState } from "../../../state";

import { EmailInput } from "../inputs/EmailInput";
import { PasswordInput } from "../inputs/PasswordInput";
import { NameInput } from "../inputs/NameInput";

import { GoogleButton } from "../buttons/GoogleButton";

import { isEmailValid, isPasswordValid } from "../validate";
import {
    Button,
    InputOtp
} from "@heroui/react";

import { ResendCode } from "../buttons/resendCode";
import { ArchiveRestore } from 'lucide-react';
import { trackEvent } from "../../../utils/pixel.js";
import { collectSignupAttribution } from "../../../utils/captureMetaParams";

export const Form = () => {
    const dispatch = useDispatch()

    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");

    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormSending, setIsFormSending] = useState(false)
    const [isFormSent, setIsFormSent] = useState(false)

    const [verificationToken, setVerificationToken] = useState("")
    const [verificationCode, setVerificationCode] = useState("")
    const [isVerificationLoading, setIsVerificationLoading] = useState(false)

    const [serverError, setServerError] = useState("")
    const [canRestoreMessage, setCanRestoreMessage] = useState("")

    const PRESENTATION_MODE = process.env.REACT_APP_PRESENTATION_MODE === 'true' ?? false

    const requestEmailVerificationCode = async () => {
        if (PRESENTATION_MODE) {
            setServerError("Sorry, aome stopped its activity. Bye bye")
            return
        }

        try {
            setIsFormSending(true)
            const response = await fetch(`${process.env.REACT_APP_API_BASE}/auth/send-verification-code`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: 'include',
                body: JSON.stringify({
                    email,
                    name,
                    password
                })
            })
            const data = await response.json();

            if (!data || typeof data !== 'object') {
                setServerError("問題が発生しました。しばらくしてからもう一度お試しください。")
                return;
            }

            if (response.ok) {
                if (data.canRestore) {
                    if (data.messageJp) {
                        setCanRestoreMessage(data.messageJp)
                    } else {
                        setCanRestoreMessage("アカウントは削除されています。復元するには、このアカウントでログインしてください。")
                    }
                    return
                }


                setVerificationToken(data.verificationToken)
                setIsFormSent(true);
            } else {
                setServerError(data.messageJp ?? "問題が発生しました。しばらくしてからもう一度お試しください。")
            }
        } catch (error) {
            setServerError("問題が発生しました。しばらくしてからもう一度お試しください。")
        } finally {
            setIsFormSending(false)
        }
    }

    const verifyEmail = async () => {
        if (PRESENTATION_MODE) return

        try {
            setIsVerificationLoading(true)
            const inviteToken = sessionStorage.getItem('inviteToken') ?? "";
            const signupAttribution = collectSignupAttribution()

            const response = await fetch(`${process.env.REACT_APP_API_BASE}/auth/verify-email`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: 'include',
                body: JSON.stringify({
                    verificationToken,
                    code: verificationCode,
                    inviteToken,
                    
                    adId: signupAttribution.adId,
                    fbp: signupAttribution.fbp,
                    fbc: signupAttribution.fbc
                })
            })

            if (response.ok) {
                const { accessToken, expiresAt, userData, isInvited } = await response.json()

                if (!accessToken || !expiresAt || !userData) {
                    setServerError("問題が発生しました。しばらくしてからもう一度お試しください。")
                    return;
                }

                dispatch(
                    setSubscriptionStatus({
                        subscriptionStatus: "trialing"
                    })
                )
                dispatch(
                    setLogin({
                        user: userData,
                        token: accessToken,
                        expiresAt: expiresAt
                    })
                );

                dispatch(setOnboardingState({ onboardingStep: "pre-onboarding" }))
            } else {
                const data = await response.json()
                setServerError(data?.messageJp ?? "問題が発生しました。しばらくしてからもう一度お試しください。")
            }
        } catch (error) {
            setServerError("問題が発生しました。しばらくしてからもう一度お試しください。")
        } finally {
            setIsVerificationLoading(false)
        }
    }

    useEffect(() => {
        setIsFormValid(isEmailValid(email) && email.length < 320 &&
            isPasswordValid(password) &&
            name && name.length < 50);
    }, [email, name, password])


    return (
        <div className="w-full h-auto lg:h-full flex flex-col lg:flex-row gap-2 items-center justify-center">
            {!isFormSent ?
                <div className="h-auto min-h-[400px] lg:h-[650px] w-full lg:w-[500px] flex flex-col justify-start lg:justify-center items-center gap-5 px-5 pt-10 lg:py-20 lg:px-10">
                    {/* <p className="text-xl font-light text-base-text">アカウント作成</p> */}

                    <div className="flex gap-2 items-center relative w-full">
                        <p className="text-xl font-light text-white text-center w-full">アカウント作成</p>
                        {/* {PRESENTATION_MODE && <p className="text-base-text font-light underline underline-offset-4">近日公開</p>} */}
                    </div>


                    <div className="w-full flex flex-col gap-2">
                        {serverError &&
                            <div className="w-full">
                                <span className="flex items-center gap-2 text-danger text-tiny w-max max-w-full rounded-md bg-danger-50 py-1 px-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-circle-x-icon lucide-circle-x text-danger"><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6" /><path d="m9 9 6 6" /></svg>
                                    {serverError}
                                </span>
                            </div>
                        }

                        {canRestoreMessage &&
                            <div className="w-full">
                                <span className="flex items-start gap-2 text-yellow-700 text-tiny w-max max-w-full rounded-md bg-yellow-500/20 py-1 px-2">
                                    <ArchiveRestore strokeWidth={1} className="size-5 text-yellow-700" />
                                    {canRestoreMessage}
                                </span>
                            </div>
                        }

                        <EmailInput email={email} setEmail={setEmail} />
                        <NameInput name={name} setName={setName} />
                        <PasswordInput password={password} setPassword={setPassword} />
                    </div>

                    <div className="flex flex-col gap-2 w-full items-center justify-center">

                        <Button
                            className="bg-[#27485C] font-light w-full text-white"
                            isDisabled={!isFormValid}
                            isLoading={isFormSending}
                            onPress={requestEmailVerificationCode}

                        >
                            次へ
                        </Button>

                        <span className="text-base-text/80 text-tiny">
                            「サインアップ」を押すことで、
                            <a href="/terms-of-service" className="underline underline-offset-6 mr-1 break-keep">利用規約</a>
                            に同意したものとみなされます。
                        </span>

                    </div>


                    <div className="w-full flex gap-2 justify-between items-center">
                        <hr className="border-none h-[1px] bg-base-text/60 flex-1"></hr>
                        <span className="font-light w-max text-base-text/90 text-center">または</span>
                        <hr className="border-none h-[1px] bg-base-text/60 flex-1"></hr>
                    </div>

                    <div className="w-full flex flex-col gap-2 justify-between items-center">
                        <GoogleButton
                            setServerError={setServerError}
                            setCanRestoreMessage={setCanRestoreMessage}
                        />
                    </div>

                </div>
                :
                <div className="h-auto min-h-[400px] lg:h-[650px] w-full lg:w-[500px] flex flex-col justify-start lg:justify-center items-center gap-5 pl-4 pr-7 pt-10 lg:py-20 lg:px-10">
                    <div className="flex flex-col w-full gap-2 items-center justify-center">
                        <p className="text-xl font-light text-white">メール認証</p>
                        <span className="font-light w-full text-white text-center text-sm">メールに送信された認証コードを入力してください。</span>
                    </div>


                    <div className="w-full flex flex-col items-center justify-center gap-3">

                        <InputOtp
                            size="lg"
                            length={6}
                            value={verificationCode}
                            onValueChange={setVerificationCode}
                            errorMessage={""}
                            classNames={{
                                segment: "font-geologica font-light text-base-text bg-base-input/40 backdrop-blur data-[hover=true]:bg-base-input/80 data-[active=true]:bg-base-input/80 group-data-[focus=true]:bg-base-input/80 shadow-none"
                            }}
                        />
                        {serverError &&
                            <div className="w-full flex flex-col items-center justify-center">
                                <span className="flex items-start gap-2 text-danger text-tiny w-max max-w-full rounded-md bg-danger-50 py-1 px-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-circle-x-icon lucide-circle-x text-danger"><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6" /><path d="m9 9 6 6" /></svg>
                                    {serverError}
                                </span>
                            </div>
                        }
                        <ResendCode email={email} name={name} password={password} setVerificationToken={setVerificationToken} />
                    </div>

                    <Button
                        className="bg-[#27485C] font-light w-full text-white"
                        isDisabled={verificationCode.length !== 6}
                        isLoading={isVerificationLoading}
                        onPress={verifyEmail}
                    >
                        確認
                    </Button>

                </div>

            }

            {/* <div className="hidden lg:flex lg:h-[650px] w-[50%] p-3">
                <div className="w-full h-full bg-[linear-gradient(164deg,_#787B97,_#C0CED9)] rounded-3xl">
                </div>
            </div> */}
            {/* <div className="hidden h-[400px] lg:flex lg:h-[650px] lg:w-[50%] p-3">
                <div className="w-full h-full flex items-center justify-center lg:pt-12">
                    <img src="landing-page/signup-aome.png" className="block h-[400px] lg:h-auto lg:max-w-[430px]" />
                </div>
            </div> */}
        </div>
    )
}